/**
 * The canonical Logic document contract: event/condition/action vocabulary
 * plus the document shape a rule set is authored and persisted as.
 *
 * This is the shared source of truth both the client (Studio's Logic mode,
 * and the generic client-side play/preview runtime) and the server
 * (sstk-sunder-realtime) build on. Previously this lived twice - a
 * hand-maintained mirror in Studio's `logicContract.ts` and the actual
 * parser in sstk-sunder-realtime's `document.ts` - kept in step by
 * discipline, not by the compiler. Moving the shared parts here means a
 * change to the contract itself only has to happen once; each host still
 * owns anything that is genuinely its own (Studio's UI labels and its
 * cutscene-flag gating, sstk-sunder-realtime's fault-isolation logging).
 */

export const LOGIC_DOCUMENT_VERSION = 2;

export const LOGIC_EVENT_KINDS = [
  'round-start',
  'round-end',
  'player-join',
  'player-leave',
  'score-change',
  'trigger-enter',
  'trigger-exit',
  /**
   * A periodic heartbeat (~1/second; see `ClientLogicRuntime`'s own
   * throttling), independent of player movement or trigger containment -
   * the one thing every other event kind here needs a real reason to fire.
   * Exists so a survival timer or any other elapsed-time win condition can
   * be authored with existing primitives alone: `{ when: 'tick', then:
   * [{ kind: 'add-variable', name: 'elapsed', value: 1 }] }` plus a second
   * rule checking that variable - no new fact or runtime concept needed.
   */
  'tick',
] as const;
export type LogicEventKind = (typeof LOGIC_EVENT_KINDS)[number];

export const LOGIC_COMPARISONS = ['eq', 'ne', 'lt', 'le', 'gt', 'ge'] as const;
export type LogicComparison = (typeof LOGIC_COMPARISONS)[number];

export const LOGIC_FACTS = [
  'round',
  'score-raiders',
  'score-wardens',
  'player-count',
  'event-team',
  'event-trigger',
  'event-player',
  'variable',
  'item-count',
  /** A named quest's current status ('inactive' when never started via
   * `start-quest'). Named like `variable`/`item-count` - `name` picks which
   * quest. Lets one quest gate another: `{ fact: 'quest-status', name:
   * 'find-key', op: 'eq', value: 'complete' }`. */
  'quest-status',
] as const;
export type LogicFactName = (typeof LOGIC_FACTS)[number];

/** Facts whose values are numbers; the rest compare as strings (eq/ne). */
export const NUMERIC_FACTS: readonly LogicFactName[] = [
  'round',
  'score-raiders',
  'score-wardens',
  'player-count',
  'variable',
  'item-count',
];

/** Facts that need a name alongside the fact (which variable, which item,
 * which quest). */
export const NAMED_FACTS: readonly LogicFactName[] = ['variable', 'item-count', 'quest-status'];

/** Valid `quest-status` values, in the only order a quest can move through
 * them (a quest never un-starts). */
export const QUEST_STATUSES = ['inactive', 'active', 'complete', 'failed'] as const;
export type QuestStatus = (typeof QUEST_STATUSES)[number];

export interface LogicCondition {
  fact: LogicFactName;
  name?: string;
  op: LogicComparison;
  value: number | string;
}

export const LOGIC_ACTION_KINDS = [
  'announce',
  'set-variable',
  'add-variable',
  'emit',
  'give-item',
  'take-item',
  'play-cutscene',
  /** Ends the session with a win, optionally scoped to a team - the runtime
   * counterpart to Game Setup's `winCondition` field. */
  'win-game',
  /** Ends the session with a loss, optionally scoped to a team. */
  'lose-game',
  /** Marks a named quest active, carrying its display title for a host's
   * own quest-log UI. Re-running it on an already-active quest is a no-op
   * (idempotent), same as authoring any other action twice. */
  'start-quest',
  /** Marks a named quest complete. A quest never explicitly started still
   * completes (the evaluator seeds it 'active' first) - a creator does not
   * have to author a matching `start-quest` for a one-shot objective. */
  'complete-quest',
  /** Marks a named quest failed - the same "started implicitly if needed"
   * behaviour as `complete-quest`. */
  'fail-quest',
] as const;
export type LogicActionKind = (typeof LOGIC_ACTION_KINDS)[number];

/**
 * Action kinds a runtime actually executes today. `'play-cutscene'` is a
 * valid, storable action kind (SF23.4 - added client-side ahead of any
 * server support) but no runtime, client or server, executes it yet; kept
 * separate from `LOGIC_ACTION_KINDS` so a host can warn when an authored
 * rule uses a kind that will silently be skipped rather than quietly not
 * running.
 *
 * Note on `'win-game'`/`'lose-game'`/`'start-quest'`/`'complete-quest'`/
 * `'fail-quest'`: `LogicEvaluator` (this package) executes these for real -
 * every Solo/Co-op session and Studio's own Preview run on it - so they
 * belong in this list. `sstk-sunder-realtime`'s authoritative Versus server
 * imports this same contract and parser from the published
 * `@sstk/build-kit/logic` package rather than a hand-copied one; until its
 * own dependency pin is bumped to a version carrying these kinds, its
 * parser's fault isolation (one bad/unknown-kind rule is dropped, not the
 * whole document - see `parser.ts`) skips them there specifically, the same
 * safe degrade `'play-cutscene'` already relies on. That pin bump is a
 * separate, deliberate step in that repo, not implied by this list.
 */
export const EXECUTABLE_ACTION_KINDS: readonly LogicActionKind[] = LOGIC_ACTION_KINDS.filter(
  (k) => k !== 'play-cutscene',
);

/** True when no runtime executes this action kind yet (SF23.4). */
export function isActionKindUnexecuted(kind: LogicActionKind): boolean {
  return !EXECUTABLE_ACTION_KINDS.includes(kind);
}

export type LogicAction =
  | { kind: 'announce'; message: string }
  | { kind: 'set-variable'; name: string; value: number }
  | { kind: 'add-variable'; name: string; value: number }
  | { kind: 'emit'; event: string }
  | { kind: 'give-item'; name: string; count: number }
  | { kind: 'take-item'; name: string; count: number }
  /** References a Cutscene.id from the 'cutscenes' mode document. See
   * `isActionKindUnexecuted` - authoring this is safe, no runtime plays it
   * yet. */
  | { kind: 'play-cutscene'; cutsceneId: string }
  /** Ends the session with a win. `team` is a free-text label (matching
   * `LogicEvent.team`'s own convention) - a solo/co-op game omits it. */
  | { kind: 'win-game'; team?: string }
  /** Ends the session with a loss. Same `team` convention as `win-game`. */
  | { kind: 'lose-game'; team?: string }
  /** Marks a quest active. `questId` is a creator-chosen stable id
   * (matched by `quest-status` conditions and `complete-quest`/
   * `fail-quest`); `title` is what a quest-log UI shows a player. */
  | { kind: 'start-quest'; questId: string; title: string }
  | { kind: 'complete-quest'; questId: string }
  | { kind: 'fail-quest'; questId: string };

/**
 * A single rule: `when` an event of this kind fires, `if` every condition
 * holds (absent/empty means unconditional), `then` run every action in
 * order. `once` fires the rule at most one time per session/match.
 */
export interface LogicRule {
  id: string;
  when: LogicEventKind;
  if?: LogicCondition[];
  then: LogicAction[];
  enabled?: boolean;
  once?: boolean;
}

export interface LogicDocument {
  version: typeof LOGIC_DOCUMENT_VERSION;
  rules: LogicRule[];
}

export class LogicDocumentError extends Error {}
